import React, { useState } from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  pointerWithin,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates
} from '@dnd-kit/sortable';
import AppIcon from './AppIcon';

function collisionDetection(args) {
  const type = args.active.data.current?.type;
  const containers = args.droppableContainers.filter((c) => {
    const t = c.data.current?.type;
    if (c.disabled) return false;
    return type === 'section' ? t === 'section' : t === 'app' || t === 'sectionDrop';
  });
  if (type === 'section') return closestCenter({ ...args, droppableContainers: containers });
  // Pointer drags drop only on a target under the pointer; keyboard drags have no pointer.
  if (!args.pointerCoordinates) return closestCenter({ ...args, droppableContainers: containers });
  return pointerWithin({ ...args, droppableContainers: containers });
}

function SortableDashboard({ sections, moveSection, moveApp, children }) {
  const [activeItem, setActiveItem] = useState(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const findSectionByApp = (id) => sections.find((s) => s.apps.some((a) => a.id === id));

  const handleDragStart = ({ active }) => {
    setActiveItem({ type: active.data.current?.type, id: active.id });
  };

  // Nothing is saved while hovering; the single write happens on drop.
  const handleDragEnd = ({ active, over }) => {
    setActiveItem(null);
    if (!over || active.id === over.id) return;
    const type = active.data.current?.type;
    const overType = over.data.current?.type;
    if (type === 'section' && overType === 'section') {
      moveSection(active.id, over.id);
    } else if (type === 'app') {
      const source = findSectionByApp(active.id);
      if (!source) return;
      if (overType === 'sectionDrop') {
        const sectionId = over.data.current.sectionId;
        if (sectionId !== source.id) moveApp(active.id, sectionId, Infinity);
      } else if (overType === 'app') {
        const target = findSectionByApp(over.id);
        if (!target) return;
        const overIndex = target.apps.findIndex((a) => a.id === over.id);
        if (target.id === source.id) {
          moveApp(active.id, target.id, overIndex);
        } else {
          const rect = active.rect.current.translated;
          const below = rect && rect.top > over.rect.top + over.rect.height / 2;
          moveApp(active.id, target.id, overIndex + (below ? 1 : 0));
        }
      }
    }
  };

  const activeApp =
    activeItem?.type === 'app'
      ? sections.flatMap((s) => s.apps).find((a) => a.id === activeItem.id)
      : null;
  const activeSection =
    activeItem?.type === 'section' ? sections.find((s) => s.id === activeItem.id) : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveItem(null)}
    >
      <SortableContext items={sections.map((s) => s.id)} strategy={rectSortingStrategy}>
        {children}
      </SortableContext>
      <DragOverlay dropAnimation={{ duration: 160, easing: 'ease-out' }}>
        {activeApp && (
          <div className="app-shortcut drag-overlay">
            <AppIcon app={activeApp} />
            <span className="app-name">{activeApp.name}</span>
          </div>
        )}
        {activeSection && (
          <div className="drag-overlay section-preview">
            <h2 className="tool-section-title">{activeSection.title}</h2>
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}

export default SortableDashboard;